// src/mazot-takip/src/pages/DolumDuzeltSil.js
import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  updateDoc,
  serverTimestamp,
  Timestamp,
  arrayUnion,
} from "firebase/firestore";

/* =======================
   Yardımcılar
======================= */
const ROLES = {
  VIEW: "view",
  ONLY_INPUT: "only_input",
  INPUT_APPROVE: "input_approve",
  CREATOR: "creator",
};

const norm = (s = "") =>
  s
    .toString()
    .toUpperCase()
    .replaceAll("İ", "I")
    .replaceAll("ı", "I")
    .replaceAll("Ş", "S")
    .replaceAll("ş", "S")
    .replaceAll("Ğ", "G")
    .replaceAll("ğ", "G")
    .replaceAll("Ü", "U")
    .replaceAll("ü", "U")
    .replaceAll("Ö", "O")
    .replaceAll("ö", "O")
    .replaceAll("Ç", "C")
    .replaceAll("ç", "C")
    .trim();

const normalizePlate = (s = "") => norm(s).replace(/[^A-Z0-9]/g, "");
const formatPlateWithSpaces = (s = "") => {
  const raw = normalizePlate(s);
  const m = raw.match(/^(\d{2})([A-Z]{1,3})(\d{2,4})$/);
  return m ? `${m[1]} ${m[2]} ${m[3]}` : raw;
};
const plateQueryValues = (s = "") =>
  Array.from(new Set([normalizePlate(s), formatPlateWithSpaces(s)].filter(Boolean)));

const parseNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const fmt = (n) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(
    Number(n || 0)
  );

const normalizeReceiptNo = (v = "") =>
  String(v || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

// Her türlü tarih değerini güvenle "YYYY-MM-DD" string'ine çevir
function toDateOnlyStr(val) {
  if (!val) return "";
  // Firestore Timestamp
  if (val && typeof val.toDate === "function") {
    const d = val.toDate();
    if (d && !isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
    return "";
  }
  // Date objesi
  if (val instanceof Date) {
    if (!isNaN(val.getTime())) {
      const y = val.getFullYear();
      const m = String(val.getMonth() + 1).padStart(2, "0");
      const day = String(val.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
    return "";
  }
  // YYYY-MM-DD string
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return val;
  }
  // ISO vb.
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return "";
}

function dateOnlyMs(val) {
  const s = toDateOnlyStr(val);
  if (!s) return null;
  const d = new Date(`${s}T12:00:00`);
  return isNaN(d.getTime()) ? null : d.getTime();
}

function isPumpVehicle(form, plates) {
  const plate = normalizePlate(form?.plaka || "");
  const vehicle = plates.find((p) => normalizePlate(p.plate) === plate);
  const type = norm(vehicle?.aracCinsi || "");
  return plate.includes("POMPA") || type.includes("POMPA");
}

/* =======================
   Rol & Listeler
======================= */
async function fetchMazotRole(email) {
  try {
    if (!email) return ROLES.VIEW;
    const snap1 = await getDoc(doc(db, "users", email));
    let data = snap1.exists() ? snap1.data() : null;

    if (!data) {
      const snap2 = await getDocs(
        query(collection(db, "users"), where("email", "==", email))
      );
      if (!snap2.empty) data = snap2.docs[0].data();
    }

    const m = data?.["mazot-takip"] || data?.["mazot_takip"] || {};
    if (m.olusturucu) return ROLES.CREATOR;
    if (m.giren_onaylayan) return ROLES.INPUT_APPROVE;
    if (m.giren) return ROLES.ONLY_INPUT;
    if (m.izleyen) return ROLES.VIEW;
    return ROLES.VIEW;
  } catch {
    return ROLES.VIEW;
  }
}

async function fetchPlates() {
  const snap = await getDocs(collection(db, "plates"));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((x) => x.active !== false)
    .map((x) => ({
      id: x.id,
      plate: normalizePlate(x.plate || ""),
      vehicleTesis: x.tesis || "",
      aracCinsi: x.aracCinsi || "",
    }))
    .filter((x) => x.plate)
    .sort((a, b) => a.plate.localeCompare(b.plate, "tr"));
}

async function fetchPeople() {
  const snap = await getDocs(collection(db, "personeller"));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((x) => x.active !== false)
    .map((x) => ({
      id: x.id,
      ad: (x.ad || "").trim(),
      soyad: (x.soyad || "").trim(),
    }))
    .filter((x) => x.ad)
    .sort((a, b) =>
      `${a.ad} ${a.soyad}`.localeCompare(`${b.ad} ${b.soyad}`, "tr")
    );
}

async function fetchTesisOptions() {
  const out = new Set();
  try {
    const s1 = await getDocs(collection(db, "tesisler"));
    s1.docs.forEach((d) => {
      const name = d.data()?.name || d.data()?.ad || d.data()?.title;
      if (name) out.add(name);
    });
  } catch {}
  try {
    const s2 = await getDocs(collection(db, "fuel_tanks"));
    s2.docs.forEach((d) => {
      const t = d.data()?.tesis;
      if (t) out.add(t);
    });
  } catch {}
  return Array.from(out).sort((a, b) => a.localeCompare(b, "tr"));
}

async function fetchTankMapByTesis() {
  const m = new Map();
  const snap = await getDocs(collection(db, "fuel_tanks"));
  snap.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((x) => x.active !== false)
    .forEach((t) => {
      const key = norm(t.tesis || "");
      if (!m.has(key)) m.set(key, []);
      m.get(key).push({ id: t.id, name: t.name || t.tankAdi || "TANK" });
    });
  return m;
}

/* =======================
   Veri getirme
======================= */
async function searchByReceipt(receipt) {
  const clean = normalizeReceiptNo(receipt);
  if (!clean) return [];
  const snap = await getDocs(
    query(
      collection(db, "fuel_transactions"),
      where("receiptNo", "==", clean)
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
}

async function fetchReadingNeighbors(plate, currentId, currentDateMs) {
  if (!plate || currentDateMs == null) return { prev: null, next: null };

  const snap = await getDocs(
    query(collection(db, "fuel_transactions"), where("vehiclePlate", "in", plateQueryValues(plate)))
  );

  let prev = null;
  let next = null;

  snap.docs.forEach((d) => {
    if (d.id === currentId) return;
    const data = d.data() || {};
    if (data.deleted === true) return;
    const dms = dateOnlyMs(data.date);
    if (dms == null) return;

    const row = { id: d.id, ...data, dms };
    if (dms < currentDateMs && (!prev || dms > prev.dms)) prev = row;
    if (dms > currentDateMs && (!next || dms < next.dms)) next = row;
  });

  return { prev, next };
}

function buildRangeMessages(label, value, prev, next, field) {
  if (value == null) return [];
  const messages = [];
  const prevValue = parseNum(prev?.[field]);
  const nextValue = parseNum(next?.[field]);
  const prevDate = toDateOnlyStr(prev?.date);
  const nextDate = toDateOnlyStr(next?.date);

  if (prev && prevValue != null && value < prevValue) {
    messages.push(
      `${label} önceki kayıttan küçük olamaz. Önceki kayıt: ${prevDate} / ${fmt(prevValue)}, girilen: ${fmt(value)}.`
    );
  }
  if (next && nextValue != null && value > nextValue) {
    messages.push(
      `${label} sonraki kayıttan büyük olamaz. Sonraki kayıt: ${nextDate} / ${fmt(nextValue)}, girilen: ${fmt(value)}.`
    );
  }

  return messages;
}

/* =======================
   Bileşen
======================= */
export default function DolumDuzeltSil() {
  const [role, setRole] = useState(null);

  const [receiptSearch, setReceiptSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState(null);

  const [plates, setPlates] = useState([]);
  const [people, setPeople] = useState([]);
  const [tesisOptions, setTesisOptions] = useState([]);
  const [tankMap, setTankMap] = useState(new Map());

  useEffect(() => {
    fetchMazotRole(auth.currentUser?.email).then(setRole);
    Promise.all([
      fetchPlates(),
      fetchPeople(),
      fetchTesisOptions(),
      fetchTankMapByTesis(),
    ]).then(([pl, pe, te, tm]) => {
      setPlates(pl);
      setPeople(pe);
      setTesisOptions(te);
      setTankMap(tm);
    });
  }, []);

  const canEdit = [ROLES.ONLY_INPUT, ROLES.INPUT_APPROVE, ROLES.CREATOR].includes(
    role
  );

  const vehicleTesisByPlate = useMemo(() => {
    const m = new Map();
    plates.forEach((p) => m.set(normalizePlate(p.plate), p.vehicleTesis || ""));
    return m;
  }, [plates]);

  const peopleByLabel = useMemo(() => {
    const m = new Map();
    people.forEach((pe) => {
      const label = `${pe.ad}${pe.soyad ? " " + pe.soyad : ""}`;
      m.set(label, pe);
    });
    return m;
  }, [people]);

  const fromDocToForm = (docData) => ({
    id: docData.id,
    deleted: !!docData.deleted,
    date: toDateOnlyStr(docData.date), // input type="date"
    tesis: docData.tesis || "",
    plaka: docData.vehiclePlate || "",
    litre: docData.liters ?? "",
    fisNo: normalizeReceiptNo(docData.receiptNo || ""),
    km: docData.odometerKm ?? "",
    saat1: docData.hour1 ?? "",
    saat2: docData.hour2 ?? "",
    soforId: docData.driverId || "",
    soforLabel: docData.driverName || "",
    vehicleTesis: docData.vehicleTesis || "",
    _raw: docData, // karşılaştırma için
  });

  const handleSearch = async () => {
    const cleanReceipt = normalizeReceiptNo(receiptSearch);
    setReceiptSearch(cleanReceipt);
    if (!cleanReceipt) {
      alert("Fiş No giriniz.");
      return;
    }
    const list = await searchByReceipt(cleanReceipt);
    setResults(list);
    setSelected(null);
    setForm(null);
    if (list.length === 0) {
      alert(`Fiş bulunamadı: ${cleanReceipt}`);
      return;
    }
    if (list.length === 1) {
      setSelected(list[0]);
      setForm(fromDocToForm(list[0]));
    }
  };

  const selectRow = (row) => {
    setSelected(row);
    setForm(fromDocToForm(row));
  };

  const onChange = (k, v) => {
    setForm((s) => {
      if (!s) return s;
      const next = { ...s, [k]: v };
      if (k === "plaka") {
        next.plaka = normalizePlate(v);
        next.vehicleTesis = vehicleTesisByPlate.get(next.plaka) || "";
      }
      if (k === "fisNo") {
        next.fisNo = normalizeReceiptNo(v);
      }
      if (k === "soforLabel") {
        const found = peopleByLabel.get(v);
        next.soforId = found ? found.id : "";
      }
      return next;
    });
  };

  // fiş no çakışması uyarısı
  const hasDuplicateReceipt = async (newReceiptNo, currentId) => {
    if (!newReceiptNo) return false;
    try {
      const snap = await getDocs(
        query(
          collection(db, "fuel_transactions"),
          where("receiptNo", "==", newReceiptNo)
        )
      );
      const others = snap.docs.filter((d) => d.id !== currentId);
      return others.length > 0;
    } catch {}
    return false;
  };

  // güvenli diff (tarih dahil)
  const diffChanges = (before, after) => {
    const keys = [
      "date",
      "tesis",
      "vehiclePlate",
      "vehicleTesis",
      "liters",
      "receiptNo",
      "odometerKm",
      "hour1",
      "hour2",
      "driverId",
      "driverName",
    ];
    const changes = {};
    keys.forEach((k) => {
      let be, af;
      if (k === "date") {
        be = toDateOnlyStr(before.date);
        af = toDateOnlyStr(after.date);
      } else {
        be = before[k] ?? "";
        af = after[k] ?? "";
      }
      if (String(be) !== String(af)) {
        changes[k] = [be, af];
      }
    });
    return changes;
  };

  const handleSave = async () => {
    if (!canEdit || !form) return;

    // Validasyon
    if (!form.date) return alert("Tarih zorunludur.");
    if (!form.tesis) return alert("Tesis zorunludur.");
    if (!form.plaka) return alert("Plaka zorunludur.");
    const liters = parseNum(form.litre);
    if (!liters || liters <= 0) return alert("Litre > 0 olmalı.");
    if (!form.soforId) return alert("Şoför seçiniz.");
    if (!form.fisNo) return alert("Fiş No zorunludur.");
    const receiptNo = normalizeReceiptNo(form.fisNo);
    if (!receiptNo) return alert("Fis No zorunludur.");
    if (!/^[A-Z0-9]+$/.test(receiptNo)) {
      return alert("Fis No sadece harf ve rakamdan olusmalidir.");
    }
    const km = parseNum(form.km);
    const currentDateMs = dateOnlyMs(form.date);
    const plate = normalizePlate(form.plaka || "");
    const neighbors = await fetchReadingNeighbors(plate, form.id, currentDateMs);
    const hour1 = parseNum(form.saat1);
    const hour2 = parseNum(form.saat2);
    const shouldCheckMachineHour = isPumpVehicle(form, plates);
    if (km === null) return alert("KM zorunludur (sayı).");

    // Fiş no çakışması
    if (currentDateMs == null) return alert("Tarih gecersiz.");

    const kmErrors = buildRangeMessages("KM", km, neighbors.prev, neighbors.next, "odometerKm");
    if (kmErrors.length) {
      alert(kmErrors.join("\n"));
      return;
    }

    const hour1Warnings = buildRangeMessages("Arac saati", hour1, neighbors.prev, neighbors.next, "hour1");
    if (hour1Warnings.length) {
      const ok = window.confirm(`${hour1Warnings.join("\n")}\n\nYine de kaydedilsin mi?`);
      if (!ok) return;
    }

    if (shouldCheckMachineHour) {
      const hour2Warnings = buildRangeMessages("Makine saati", hour2, neighbors.prev, neighbors.next, "hour2");
      if (hour2Warnings.length) {
        const ok = window.confirm(`${hour2Warnings.join("\n")}\n\nYine de kaydedilsin mi?`);
        if (!ok) return;
      }
    }

    const duplicateReceipt = await hasDuplicateReceipt(receiptNo, form.id);
    if (duplicateReceipt) {
      alert(`Bu fis no (${receiptNo}) baska kayitta var. Ayni fis numarasiyla kayit yapilamaz.`);
      return;
    }

    // Tesis -> tank eşleşmesi (her tesiste tek tank varsayımı)
    const arr = tankMap.get(norm(form.tesis)) || [];
    const tank = arr.length ? arr[0] : null;

    // Payload
    const payload = {
      date: Timestamp.fromDate(new Date(`${form.date}T12:00:00`)),
      tesis: form.tesis,
      tankId: tank ? tank.id : null,
      vehiclePlate: plate,
      vehicleTesis: form.vehicleTesis || "",
      liters,
      receiptNo,
      odometerKm: km,
      hour1,
      hour2: shouldCheckMachineHour ? hour2 : null,
      driverId: form.soforId,
      driverName: form.soforLabel || null,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.email || "anonim",
    };

    // Diff (audit)
    const before = { ...form._raw };
    const afterForDiff = {
      ...before,
      date: payload.date,
      tesis: payload.tesis,
      vehiclePlate: payload.vehiclePlate,
      vehicleTesis: payload.vehicleTesis,
      liters: payload.liters,
      receiptNo: payload.receiptNo,
      odometerKm: payload.odometerKm,
      hour1: payload.hour1,
      hour2: payload.hour2,
      driverId: payload.driverId,
      driverName: payload.driverName,
    };
    const changes = diffChanges(before, afterForDiff);

    // Güncelle (history içine serverTimestamp KOYMAYACAĞIZ; Timestamp.now() kullan)
    const ref = doc(db, "fuel_transactions", form.id);
    await updateDoc(ref, {
      ...payload,
      history: arrayUnion({
        action: "update",
        by: auth.currentUser?.email || "anonim",
        at: Timestamp.now(), // <<— ÖNEMLİ: arrayUnion içinde serverTimestamp() kullanmayız
        changes,
      }),
    });

    alert("Kayıt güncellendi ✅");

    // Ekranı güncelle (yerel kopyayı yeni alanlarla güncelliyoruz)
    const newRaw = { ...afterForDiff, id: form.id, deleted: before.deleted };
    setSelected((prev) => prev ? { ...prev, ...newRaw } : newRaw);
    setForm(fromDocToForm(newRaw));
  };

  const handleSoftDelete = async () => {
    if (!canEdit || !form) return;
    const ok = window.confirm(
      "Bu kaydı silmek istediğinize emin misiniz? (Soft delete – geri alınabilir)"
    );
    if (!ok) return;

    const ref = doc(db, "fuel_transactions", form.id);
    await updateDoc(ref, {
      deleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: auth.currentUser?.email || "anonim",
      history: arrayUnion({
        action: "delete",
        by: auth.currentUser?.email || "anonim",
        at: Timestamp.now(), // <<—
      }),
    });

    alert("Kayıt soft silindi 🗑️");
    const newRaw = { ...form._raw, id: form.id, deleted: true };
    setSelected((prev) => prev ? { ...prev, deleted: true } : newRaw);
    setForm((f) => (f ? { ...f, deleted: true } : f));
  };

  const handleRestore = async () => {
    if (!canEdit || !form) return;
    const ok = window.confirm("Silinen kaydı geri getirmek istiyor musunuz?");
    if (!ok) return;

    const ref = doc(db, "fuel_transactions", form.id);
    await updateDoc(ref, {
      deleted: false,
      restoredAt: serverTimestamp(),
      restoredBy: auth.currentUser?.email || "anonim",
      history: arrayUnion({
        action: "restore",
        by: auth.currentUser?.email || "anonim",
        at: Timestamp.now(), // <<—
      }),
    });

    alert("Kayıt geri alındı ♻️");
    const newRaw = { ...form._raw, id: form.id, deleted: false };
    setSelected((prev) => prev ? { ...prev, deleted: false } : newRaw);
    setForm((f) => (f ? { ...f, deleted: false } : f));
  };

  /* =======================
     Geçmişi (history) okunur gösterim
  ======================= */
  const ACTION_LABEL = {
    update: "Güncelleme",
    delete: "Silme",
    restore: "Geri Alma",
  };
  const FIELD_LABEL = {
    date: "Tarih",
    tesis: "Tesis",
    vehiclePlate: "Plaka",
    vehicleTesis: "Araç Tesisi",
    liters: "Litre",
    receiptNo: "Fiş No",
    odometerKm: "KM",
    hour1: "Araç Saati",
    hour2: "Makine Saati",
    driverId: "Şoför ID",
    driverName: "Şoför",
  };
  function prettyValue(key, v) {
    if (key === "date") return toDateOnlyStr(v) || "-";
    if (key === "liters" || key === "odometerKm") {
      const n = parseNum(v);
      return n === null ? "-" : fmt(n);
    }
    if (key === "receiptNo") {
      const s = (v ?? "").toString();
      return s ? s.toLowerCase() : "-";
    }
    return (v ?? "").toString() || "-";
  }
  function renderChangesObj(changes) {
    if (!changes || typeof changes !== "object") return "-";
    const lines = [];
    Object.entries(changes).forEach(([k, arr]) => {
      const label = FIELD_LABEL[k] || k;
      const [oldV, newV] = Array.isArray(arr) ? arr : [null, null];
      const oldS = prettyValue(k, oldV);
      const newS = prettyValue(k, newV);
      // Fiş No örneğindeki gibi tek tırnakla göster
      const wrap = (s) => `'${s}'`;
      lines.push(`${label}: ${wrap(oldS)} → ${wrap(newS)}`);
    });
    return lines.join("\n");
  }

  /* =======================
     UI
  ======================= */
  return (
    <div style={{ padding: 20 }}>
      <h2
        style={{
          textAlign: "left",
          color: "#334155",
          fontSize: 22,
          fontWeight: "bold",
          marginBottom: 16,
        }}
      >
        Dolum Düzelt / Soft Sil – Geri Al
      </h2>

      {/* Arama */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 8,
          maxWidth: 560,
          marginBottom: 16,
        }}
      >
        <input
          placeholder="Fiş No girin (örn: G123)"
          value={receiptSearch}
          onChange={(e) => setReceiptSearch(normalizeReceiptNo(e.target.value))}
          onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
          style={{ border: "1px solid #cbd5e1", padding: 10, borderRadius: 6 }}
        />
        <button
          onClick={handleSearch}
          style={{
            background: "#0ea5e9",
            color: "#fff",
            padding: "10px 16px",
            border: "none",
            borderRadius: 6,
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Getir
        </button>
      </div>

      {/* Çok sonuç varsa liste */}
      {results.length > 1 && (
        <div style={{ overflowX: "auto", marginBottom: 16 }}>
          <table style={{ borderCollapse: "collapse", minWidth: 820, fontSize: 13 }}>
            <thead style={{ background: "#e2e8f0" }}>
              <tr>
                {["Seç", "ID", "Tarih", "Plaka", "Litre", "Tesis", "Şoför", "Silinmiş mi?"].map(
                  (h, i) => (
                    <th
                      key={i}
                      style={{
                        border: "1px solid #cbd5e1",
                        padding: 6,
                        textAlign: "center",
                      }}
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {results.map((r) => {
                const d = toDateOnlyStr(r.date);
                return (
                  <tr
                    key={r.id}
                    style={{
                      background: selected?.id === r.id ? "#eef2ff" : "transparent",
                    }}
                  >
                    <td
                      style={{
                        border: "1px solid #e5e7eb",
                        padding: 6,
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => selectRow(r)}
                        style={{
                          padding: "4px 8px",
                          border: "1px solid #94a3b8",
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        Seç
                      </button>
                    </td>
                    <td
                      style={{
                        border: "1px solid #e5e7eb",
                        padding: 6,
                        fontFamily: "monospace",
                      }}
                    >
                      {r.id}
                    </td>
                    <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>{d}</td>
                    <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                      {r.vehiclePlate}
                    </td>
                    <td
                      style={{
                        border: "1px solid #e5e7eb",
                        padding: 6,
                        textAlign: "right",
                      }}
                    >
                      {fmt(r.liters)}
                    </td>
                    <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                      {r.tesis || "-"}
                    </td>
                    <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                      {r.driverName || "-"}
                    </td>
                    <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                      {r.deleted ? "Evet" : "Hayır"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form */}
      {form && (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)", // Her satırda tam 4 kutu olacak şekilde sabitler
              gap: "20px 15px",
            }}
          >
            <div>
              <label className="block text-sm">Tarih</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => onChange("date", e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  padding: 8,
                  borderRadius: 6,
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label className="block text-sm">Tesis</label>
              <input
                list="dl-tesisler"
                value={form.tesis}
                onChange={(e) => onChange("tesis", e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  padding: 8,
                  borderRadius: 6,
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label className="block text-sm">Plaka</label>
              <input
                list="dl-plakalar"
                value={form.plaka}
                onChange={(e) => onChange("plaka", e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  padding: 8,
                  borderRadius: 6,
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label className="block text-sm">Litre</label>
              <input
                type="text"
                value={form.litre}
                onChange={(e) => onChange("litre", e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  padding: 8,
                  borderRadius: 6,
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label className="block text-sm">Şoför</label>
              <input
                list="dl-soforler"
                value={form.soforLabel}
                onChange={(e) => onChange("soforLabel", e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  padding: 8,
                  borderRadius: 6,
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label className="block text-sm">Fiş No</label>
              <input
                value={form.fisNo}
                onChange={(e) => onChange("fisNo", e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  padding: 8,
                  borderRadius: 6,
                  fontFamily: "monospace",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label className="block text-sm">KM</label>
              <input
                value={form.km}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && !/^\d*$/.test(v)) return;
                  onChange("km", v);
                }}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  padding: 8,
                  borderRadius: 6,
                  boxSizing: "border-box",
                }}
              />
            </div>

<div>
  <label className="block text-sm">Araç Saati</label>
  <input
    value={form.saat1}
    onChange={(e) => onChange("saat1", e.target.value)}
    style={{
      width: "100%",
      border: "1px solid #cbd5e1",
      padding: 8,
      borderRadius: 6,
      boxSizing: "border-box",
    }}
  />
</div>

<div>
  <label style={{ display: "block", fontSize: "14px", fontWeight: "600", marginBottom: "5px", color: "#475569" }}>
    Makine Saati
  </label>
  <input
    value={form.saat2}
    /* Burada dedik ki: Plakada "POMPA" yazıyorsa VEYA veritabanında bu aracın cinsi "POMPA" ise kilidi aç */
    disabled={
      !( (form.plaka || "").includes("POMPA") || 
         (plates.find(p => normalizePlate(p.plate) === normalizePlate(form.plaka))?.aracCinsi || "").toUpperCase().includes("POMPA") )
    }
    onChange={(e) => onChange("saat2", e.target.value)}
    placeholder={
      ((form.plaka || "").includes("POMPA") || 
      (plates.find(p => normalizePlate(p.plate) === normalizePlate(form.plaka))?.aracCinsi || "").toUpperCase().includes("POMPA")) 
      ? "0.00" : "Kilitli"
    }
    style={{
      width: "100%",
      border: "1px solid #cbd5e1",
      padding: "10px",
      borderRadius: "6px",
      fontSize: "14px",
      /* Kilitliyse arka plan gri, açıksa beyaz */
      background: !( (form.plaka || "").includes("POMPA") || 
                     (plates.find(p => normalizePlate(p.plate) === normalizePlate(form.plaka))?.aracCinsi || "").toUpperCase().includes("POMPA") ) 
                  ? "#f1f5f9" : "#fff",
      color: !( (form.plaka || "").includes("POMPA") || 
                (plates.find(p => normalizePlate(p.plate) === normalizePlate(form.plaka))?.aracCinsi || "").toUpperCase().includes("POMPA") ) 
             ? "#94a3b8" : "#000",
      cursor: !( (form.plaka || "").includes("POMPA") || 
                 (plates.find(p => normalizePlate(p.plate) === normalizePlate(form.plaka))?.aracCinsi || "").toUpperCase().includes("POMPA") ) 
              ? "not-allowed" : "text",
      boxSizing: "border-box",
    }}
  />
</div>

            <div>
              <label className="block text-sm">Araç Tesisi (bilgi)</label>
              <input
                value={form.vehicleTesis || ""}
                readOnly
                style={{
                  width: "100%",
                  border: "1px dashed #cbd5e1",
                  padding: 8,
                  borderRadius: 6,
                  background: "#f8fafc",
                  color: "#334155",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* Datalist kaynakları */}
          <datalist id="dl-plakalar">
            {plates.map((p) => (
              <option key={p.id} value={p.plate} />
            ))}
          </datalist>
          <datalist id="dl-soforler">
            {people.map((pe) => (
              <option
                key={pe.id}
                value={`${pe.ad}${pe.soyad ? " " + pe.soyad : ""}`}
              />
            ))}
          </datalist>
          <datalist id="dl-tesisler">
            {tesisOptions.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>

          {/* İşlem butonları */}
          <div style={{ marginTop: "30px", display: "flex", gap: "12px", borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
            <button
              onClick={handleSave}
              disabled={!canEdit}
              style={{
                background: "#4f46e5",
                color: "#fff",
                padding: "10px 16px",
                border: "none",
                borderRadius: 6,
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              💾 Kaydet
            </button>

            {!form.deleted ? (
              <button
                onClick={handleSoftDelete}
                disabled={!canEdit}
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  padding: "10px 16px",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: "bold",
                }}
              >
                🗑️ Soft Sil
              </button>
            ) : (
              <button
                onClick={handleRestore}
                disabled={!canEdit}
                style={{
                  background: "#22c55e",
                  color: "#fff",
                  padding: "10px 16px",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: "bold",
                }}
              >
                ♻️ Geri Al
              </button>
            )}
          </div>

          {/* Geçmiş */}
          <div style={{ marginTop: 16 }}>
            <h3 style={{ margin: "12px 0", color: "#0f172a" }}>Geçmiş (history)</h3>
            {Array.isArray(selected?.history) && selected.history.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{ borderCollapse: "collapse", minWidth: 720, fontSize: 13 }}
                >
                  <thead style={{ background: "#e2e8f0" }}>
                    <tr>
                      {["İşlem", "Kullanıcı", "Zaman", "Değişiklikler"].map((h, i) => (
                        <th
                          key={i}
                          style={{
                            border: "1px solid #cbd5e1",
                            padding: 6,
                            textAlign: "center",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selected.history.map((h, idx) => (
                      <tr key={idx}>
                        <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                          {ACTION_LABEL[h.action] || h.action || "-"}
                        </td>
                        <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                          {h.by || "-"}
                        </td>
                        <td style={{ border: "1px solid #e5e7eb", padding: 6 }}>
                          {h.at?.toDate
                            ? h.at.toDate().toLocaleString("tr-TR")
                            : "-"}
                        </td>
                        <td
                          style={{
                            border: "1px solid #e5e7eb",
                            padding: 6,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {renderChangesObj(h.changes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#64748b" }}>
                Bu kayıt için geçmiş bulunamadı. (Güncelledikçe/sildikçe burada birikir)
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
